import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Breadcrumbs, Button, CircularProgress, Link, Typography } from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import axios from "axios";
import HeaderDashboard from "../../components/HeaderDashboard";
import ModalNotificacao from "./ModalNotificacao";
import AlertSuccess from "../../components/AlertSuccess";
import AlertError from "../../components/AlertError";
import { useUser } from "../../../contexts/UserContext";
import DocumentosSocios from "./DocumentosSocios";
import { botaoPrincipalSx, cardSx } from "./EstilosFicha";

// Documentos são enviados por sócio; sem sócios cadastrados, direciona para a ficha cadastral
const Documentos = () => {
    const { user } = useUser();
    const navigate = useNavigate();

    const [userActive, setUserActive] = useState(true);
    const [openModalNotificacao, setOpenModalNotificacao] = useState(false);
    const [notificacoes, setNotificacoes] = useState([]);
    const [socios, setSocios] = useState([]);
    const [carregando, setCarregando] = useState(true);
    const [openSnackbarSuccess, setOpenSnackbarSuccess] = useState(false);
    const [openSnackbarError, setOpenSnackbarError] = useState(false);
    const [mensagem, setMensagem] = useState("");

    useEffect(() => {
        const hasFaturasVencidas = user?.faturas?.vencidas?.length > 0;
        setUserActive(!hasFaturasVencidas);
    }, [user]);

    useEffect(() => {
        const getNotify = async () => {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/buscarNotificacoes`, { email: localStorage.getItem("email") }, {
                headers: {
                    Authorization: `${localStorage.getItem("token")}`
                }
            });

            if (response.data.status === 200) {
                setNotificacoes(response.data.notificacoes)
            }
        }

        getNotify();
    }, []);

    const getSocios = useCallback(async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/listarSocios`, {
                headers: {
                    Authorization: localStorage.getItem("token")
                }
            });
            if (response.data.status === 200) {
                setSocios(response.data.socios ?? []);
            }
        } catch (error) {
            setMensagem("Não foi possível carregar os documentos.");
            setOpenSnackbarError(true);
        } finally {
            setCarregando(false);
        }
    }, []);

    useEffect(() => {
        getSocios();
    }, [getSocios]);

    const sucesso = (texto) => {
        setMensagem(texto);
        setOpenSnackbarSuccess(true);
    };

    const erro = (texto) => {
        setMensagem(texto);
        setOpenSnackbarError(true);
    };

    return (
        <>
            <HeaderDashboard userActive={userActive} setOpenModalNotificacao={setOpenModalNotificacao} notificacoes={notificacoes.length} />
            <Box sx={{ pt: "80px", pb: 4, px: { xs: 2, md: "5%" }, minHeight: "100vh" }}>
                <Box sx={{ ...cardSx, display: "flex", flexDirection: { xs: "column", md: "row" }, alignItems: { xs: "stretch", md: "center" }, justifyContent: "space-between", gap: 2 }}>
                    <Box>
                        <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: 12, "& .MuiBreadcrumbs-separator": { mx: 0.5 } }}>
                            <Link
                                underline="hover"
                                sx={{ display: "flex", alignItems: "center", cursor: "pointer", fontSize: 12, color: "#6b7280" }}
                                onClick={() => navigate("../Dashboard")}
                            >
                                <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                                Dashboard
                            </Link>
                            <Typography sx={{ fontSize: 12, color: "#111827", fontWeight: 600, display: "flex", alignItems: "center" }}>
                                <InsertDriveFileIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                                Documentos
                            </Typography>
                        </Breadcrumbs>
                        <Typography sx={{ mt: 1, fontSize: { xs: 20, md: 24 }, fontWeight: 700, color: "#111827" }}>
                            Documentos
                        </Typography>
                        <Typography sx={{ mt: 0.5, fontSize: 14, color: "#4b5563" }}>
                            Envie os documentos de cada sócio para concluir o cadastro da empresa.
                        </Typography>
                    </Box>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate("../Dashboard")}
                        sx={{ textTransform: "none", fontSize: 13, fontWeight: 600, color: "#374151", backgroundColor: "#f3f4f6", borderRadius: "8px", px: 2, "&:hover": { backgroundColor: "#e5e7eb" } }}
                    >
                        Voltar
                    </Button>
                </Box>

                <Box sx={{ mt: 2 }}>
                    {carregando ? (
                        <Box sx={{ ...cardSx, display: "flex", justifyContent: "center", py: 8 }}>
                            <CircularProgress size={28} sx={{ color: "#0b243d" }} />
                        </Box>
                    ) : socios.length > 0 ? (
                        <DocumentosSocios socios={socios} onAtualizar={getSocios} onSucesso={sucesso} onErro={erro} />
                    ) : (
                        <Box sx={{ ...cardSx, textAlign: "center", py: 6 }}>
                            <GroupsOutlinedIcon sx={{ fontSize: 36, color: "#d1d5db" }} />
                            <Typography sx={{ mt: 1, fontSize: 14, fontWeight: 600, color: "#374151" }}>
                                Nenhum sócio cadastrado.
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: "#6b7280" }}>
                                Os documentos são enviados por sócio. Preencha a ficha cadastral e adicione os sócios primeiro.
                            </Typography>
                            <Button variant="contained" onClick={() => navigate("../Dashboard/FichaCadastral")} sx={{ ...botaoPrincipalSx, mt: 2 }}>
                                Ir para a ficha cadastral
                            </Button>
                        </Box>
                    )}
                </Box>
            </Box>
            <ModalNotificacao
                openModalNotificacao={openModalNotificacao}
                onClose={() => setOpenModalNotificacao(false)}
                notificacoes={notificacoes}
            />
            <AlertSuccess openSnackbarSuccess={openSnackbarSuccess} setOpenSnackbarSuccess={setOpenSnackbarSuccess} mensagem={mensagem} />
            <AlertError openSnackbarError={openSnackbarError} setOpenSnackbarError={setOpenSnackbarError} mensagem={mensagem} />
        </>
    );
};

export default Documentos;
